import React, { PureComponent } from 'react';
import { inject, observer } from 'mobx-react';
import { UserStore } from '../../core/user-store';
import { EMAIL_REGEXP, PAGES_BASE_PATHNAMES } from '../../core/consts';
import { submitOkta, generateTokensOkta, verifyMfaFactor } from '../../core/okta-auth-api';
import { OktaMinimalPasswordLength, OktaScopes } from '../../core/okta-auth-api.consts';
import MfaVerification from '../../components/mfa-verification/mfa-verification';
import { parseFragment } from '../../core/fragment.utils';
import { captureError } from '../../core/error-handling';
import '../sign-up/SignUpPage.scss';
import { getOktaErrorMessage } from '../../core/okta-auth-api.utils';
import { ACCESS_TOKEN_COOKIE, redirectToLogin, REFRESH_TOKEN_COOKIE } from '../../core/authentication';
import history from '../../core/history';
import { SessionStorageKeys } from '../../core/session-storage';
import { withTranslation, WithTranslation } from 'react-i18next';
import { track, TrackedEvent } from '../../core/analytics';

interface StoresProps {
  userStore?: UserStore;
}

type Props = StoresProps & WithTranslation;

interface State {
  isEmailValid: boolean;
  signInClicked: boolean;
  mfaRequired: boolean;
  mfaVerifying: boolean;
  mfaErrorMessage: string;
  mfaFactors: any[];
  selectedFactorId: string;
  mfaSuccess: boolean;
}

const LoginPage = inject('userStore')(
  observer(
    withTranslation()(
      //@ts-ignore
      class LoginPage extends PureComponent<Props, State> {
        emailInputEl: HTMLInputElement;
        passwordInputEl: HTMLInputElement;
        loginButtonEl: HTMLButtonElement;
        formEl: HTMLFormElement;
        state: State = {
          isEmailValid: null,
          signInClicked: false,
          mfaRequired: false,
          mfaVerifying: false,
          mfaErrorMessage: '',
          mfaFactors: [],
          selectedFactorId: '',
          mfaSuccess: false,
        };
        isDisplayApiTokensRequestUi: boolean;
        isHideUi: boolean;

        constructor(props: Props) {
          super(props);

          const { requestedOktaScope } = props.userStore;

          this.isDisplayApiTokensRequestUi = true;

          const access_token = sessionStorage.getItem(ACCESS_TOKEN_COOKIE);
          const refresh_token = sessionStorage.getItem(REFRESH_TOKEN_COOKIE);

          // Check if we're in the middle of MFA redirection
          const mfaRedirecting = sessionStorage.getItem(SessionStorageKeys.MFA_REDIRECTING);
          if (mfaRedirecting === 'true') {
            this.state.mfaRequired = true;
            this.state.mfaVerifying = true;
          }

          if (access_token || refresh_token) {
            sessionStorage.removeItem(ACCESS_TOKEN_COOKIE);
            sessionStorage.removeItem(REFRESH_TOKEN_COOKIE);
            sessionStorage.removeItem(SessionStorageKeys.REQUESTED_OKTA_SCOPE);
            // Clear MFA redirecting flag after successful auth
            sessionStorage.removeItem(SessionStorageKeys.MFA_REDIRECTING);
          }

          const parsedFragment: { [key: string]: string } = parseFragment() || {};

          const { code: oktaCode, error: oktaErrorCode, error_description: oktaErrorDescription } = parsedFragment;

          if (
            process.env.REACT_APP_IS_EXTERNAL_LOGIN_ENABLED === 'true' &&
            !(this.isDisplayApiTokensRequestUi || oktaCode || !!(access_token && refresh_token))
          ) {
            redirectToLogin();
            return;
          }

          if (oktaCode) {
            this.isHideUi = true;
            generateTokensOkta(oktaCode);
          } else if (access_token && refresh_token) {
            this.isHideUi = true;

            this.props.userStore.setUserApiTokens(
              {
                accessToken: access_token,
                refreshToken: refresh_token,
              },
              requestedOktaScope
            );

            setTimeout(() => {
              history.push(PAGES_BASE_PATHNAMES.Dashboard);
            }, 0);
          }

          if (oktaErrorCode) {
            captureError({ oktaErrorCode, oktaErrorDescription });
            const appliedInputEl: HTMLInputElement = this.isDisplayApiTokensRequestUi
              ? this.passwordInputEl
              : this.emailInputEl;
            appliedInputEl.classList.add('has-error');
            appliedInputEl.nextElementSibling.nextElementSibling.innerHTML = getOktaErrorMessage(
              {
                errorCode: oktaErrorCode,
              },
              this.isDisplayApiTokensRequestUi
            );
            appliedInputEl.nextElementSibling.nextElementSibling.classList.remove('toggle-content');
            this.EnableFormFields();
          }
        }

        DocumentKeyPressHandler = (e: KeyboardEvent) => {
          // Only handle Enter key if login button exists and we're not in MFA mode
          if (e.key === 'Enter' && !this.state.mfaRequired && this.loginButtonRef.current) {
            e.preventDefault();
            this.loginButtonRef.current.click();
          }
        };

        ResetFormErrors = () => {
          // Only reset errors if elements exist
          if (!this.isDisplayApiTokensRequestUi && this.emailInputRef.current) {
            const emailInput = this.emailInputRef.current;
            emailInput.classList.remove('has-error');
            
            // Safely access siblings
            const errorElement = emailInput.nextElementSibling?.nextElementSibling;
            if (errorElement) {
              errorElement.classList.add('toggle-content');
            }
          }
          
          if (this.passwordInputRef.current) {
            const passwordInput = this.passwordInputRef.current;
            passwordInput.classList.remove('has-error');
            
            // Safely access siblings
            const errorElement = passwordInput.nextElementSibling?.nextElementSibling;
            if (errorElement) {
              errorElement.classList.add('toggle-content');
            }
          }
        };

        ValidateForm = () => {
          if (!this.loginButtonRef.current || !this.passwordInputRef.current) {
            return;
          }

          const isEmailValid = EMAIL_REGEXP.test(
            this.isDisplayApiTokensRequestUi
              ? this.props.userStore.userData.$email
              : this.emailInputRef.current?.value?.toLocaleLowerCase() || ''
          );
          
          const isPasswordValid =
            this.passwordInputRef.current.value && 
            this.passwordInputRef.current.value.length >= OktaMinimalPasswordLength;
          
          if (isEmailValid && isPasswordValid) {
            this.loginButtonRef.current.disabled = false;
          } else {
            this.loginButtonRef.current.disabled = true;
          }
        };

        EnableFormFields = () => {
          // Only enable fields if they exist
          if (this.passwordInputRef.current) {
            this.passwordInputRef.current.disabled = false;
          }
          
          if (!this.isDisplayApiTokensRequestUi && this.emailInputRef.current) {
            this.emailInputRef.current.disabled = false;
          }
          
          if (this.loginButtonRef.current) {
            this.loginButtonRef.current.classList.remove('btn-in-process');
          }
        };

        DisableFormFields = () => {
          // Only disable fields if they exist
          if (this.passwordInputRef.current) {
            this.passwordInputRef.current.disabled = true;
            this.passwordInputRef.current.value = '';
          }
          
          if (!this.isDisplayApiTokensRequestUi && this.emailInputRef.current) {
            this.emailInputRef.current.disabled = true;
          }
        };

        HandleFormInput = (e) => {
          this.ResetFormErrors();
          this.ValidateForm();
        };

        HandleLoginClick = async () => {
          // Check if button ref exists
          if (!this.loginButtonRef.current || !this.passwordInputRef.current) {
            return;
          }

          this.loginButtonRef.current.disabled = true;
          this.loginButtonRef.current.classList.add('btn-in-process');

          if (this.isDisplayApiTokensRequestUi) {
            track(TrackedEvent.apiAccessGenerateTokensClicked);
          }

          const password = this.passwordInputRef.current.value;
          const email = this.isDisplayApiTokensRequestUi
            ? this.props.userStore.userData.$email
            : this.emailInputRef.current?.value || '';

          this.DisableFormFields();

          const oktaResponse = await submitOkta(email, password, this.props.userStore.requestedOktaScope);

          // Handle MFA required status
          if (oktaResponse && oktaResponse.status === 'MFA_REQUIRED') {
            const factors = oktaResponse._embedded?.factors || [];
            const totpFactor = factors.find((factor) => factor.factorType === 'token:software:totp');

            this.setState({
              signInClicked: false,
              mfaRequired: true,
              mfaFactors: factors,
              selectedFactorId: totpFactor ? totpFactor.id : '',
            });
            return;
          }

          // Handle error
          if (oktaResponse && !oktaResponse.sessionToken) {
            // Determine which input element to show error on
            const appliedInputRef = this.isDisplayApiTokensRequestUi
              ? this.passwordInputRef
              : this.emailInputRef;
              
            if (appliedInputRef.current) {
              appliedInputRef.current.classList.add('has-error');
              
              // Safely access siblings
              const errorElement = appliedInputRef.current.nextElementSibling?.nextElementSibling;
              if (errorElement) {
                errorElement.innerHTML = getOktaErrorMessage(
                  oktaResponse,
                  this.isDisplayApiTokensRequestUi
                );
                errorElement.classList.remove('toggle-content');
              }
            }
            
            this.EnableFormFields();
          }
        };

        handleMfaVerify = async (code: string) => {
          const { selectedFactorId } = this.state;

          if (!selectedFactorId) {
            this.setState({
              mfaErrorMessage: 'No authentication factor selected',
            });
            return;
          }

          this.setState({
            mfaVerifying: true,
            mfaErrorMessage: '',
          });

          try {
            const verificationResponse = await verifyMfaFactor(
              selectedFactorId,
              code,
              this.props.userStore.requestedOktaScope
            );

            if (
              verificationResponse.error ||
              (verificationResponse.status !== 'SUCCESS' && !verificationResponse.sessionToken)
            ) {
              // Only update state if there's an error
              this.setState({
                mfaVerifying: false,
                mfaErrorMessage: verificationResponse.error || 'Verification failed. Please try again.',
              });
            } else if (verificationResponse.status === 'SUCCESS') {
              // Set success state for green borders
              this.setState({
                mfaSuccess: true
              });
              // The redirect will happen automatically from the API call after a short delay
            }
          } catch (error) {
            this.setState({
              mfaVerifying: false,
              mfaErrorMessage: 'Failed to verify code. Please try again.',
            });
            captureError(error);
          }
        };

        // Create refs for form elements instead of using querySelector
        formRef = React.createRef<HTMLFormElement>();
        emailInputRef = React.createRef<HTMLInputElement>();
        passwordInputRef = React.createRef<HTMLInputElement>();
        loginButtonRef = React.createRef<HTMLButtonElement>();
        
        BindEventHandlers = () => {
          // Only bind events if we're not in MFA mode and the form exists
          if (!this.state.mfaRequired && this.formRef.current) {
            this.formEl = this.formRef.current;
            
            if (!this.isDisplayApiTokensRequestUi && this.emailInputRef.current) {
              this.emailInputEl = this.emailInputRef.current;
            }
            
            if (this.passwordInputRef.current) {
              this.passwordInputEl = this.passwordInputRef.current;
              this.formEl.addEventListener('input', this.HandleFormInput);
            }
            
            if (this.loginButtonRef.current) {
              this.loginButtonEl = this.loginButtonRef.current;
              this.loginButtonEl.addEventListener('click', this.HandleLoginClick);
            }
            
            document.addEventListener('keypress', this.DocumentKeyPressHandler, true);
            
            this.EnableFormFields();
          }
        };

        componentDidMount() {
          if (this.isHideUi) {
            return;
          }
          this.BindEventHandlers();

          // Clear MFA redirecting flag if it exists but we're not in MFA verification state
          if (!this.state.mfaRequired) {
            sessionStorage.removeItem(SessionStorageKeys.MFA_REDIRECTING);
          }
        }

        componentWillUnmount() {
          if (this.isHideUi) {
            return;
          }
          
          // Safely remove event listeners only if elements exist
          if (this.formEl) {
            this.formEl.removeEventListener('input', this.HandleFormInput);
          }
          
          if (this.loginButtonEl) {
            this.loginButtonEl.removeEventListener('click', this.HandleLoginClick);
          }
          
          // Always remove document event listeners
          document.removeEventListener('keypress', this.DocumentKeyPressHandler, true);
        }

        render() {
          if (this.isHideUi) {
            return null;
          }

          const { t } = this.props;
          const { mfaRequired, mfaVerifying, mfaErrorMessage, mfaSuccess } = this.state;

          return (
            <div className="ndp-auth-page">
              <div className="inner">
                {mfaRequired ? (
                  <MfaVerification
                    onVerify={this.handleMfaVerify}
                    isVerifying={mfaVerifying}
                    error={!!mfaErrorMessage}
                    errorMessage={mfaErrorMessage}
                    success={mfaSuccess}
                  />
                ) : (
                  <>
                    {this.isDisplayApiTokensRequestUi ? (
                      <p style={{ fontWeight: 400 }}>
                        You're a step away from tokens delivery!
                        <br />
                        Please re-enter password to proceed
                      </p>
                    ) : (
                      <p className="title">Sign in to your account</p>
                    )}

                    <form id="login" ref={this.formRef}>
                      {!this.isDisplayApiTokensRequestUi && (
                        <React.Fragment>
                          <input 
                            id="email" 
                            type="text" 
                            name="email" 
                            disabled 
                            ref={this.emailInputRef}
                          />
                          <label htmlFor="email">{t("loginPage.email")}</label>
                          <p className="field-error toggle-content"></p>
                        </React.Fragment>
                      )}

                      <input 
                        id="password" 
                        type="password" 
                        name="password" 
                        disabled 
                        ref={this.passwordInputRef}
                      />
                      <label htmlFor="password">{t("loginPage.password")}</label>
                      <p className="field-error toggle-content"></p>

                      <button 
                        id="login-button" 
                        className="btn btn-primary" 
                        type="button" 
                        disabled
                        ref={this.loginButtonRef}
                      >
                        {t(this.isDisplayApiTokensRequestUi ? "loginPage.generateTokens" : "loginPage.signIn")}
                      </button>
                    </form>
                  </>
                )}

                {!!(process.env.REACT_APP_IS_STAGED_CONTENT_ONLY !== 'true' && !this.isDisplayApiTokensRequestUi) && (
                  <p>
                    Don't have an account?&nbsp;
                    <a href={PAGES_BASE_PATHNAMES.SignUp} className="color-text_highlighted">
                      Register here
                    </a>
                    .
                  </p>
                )}
                {!this.isDisplayApiTokensRequestUi && (
                  <section className="terms-of-service">
                    I acknowledge that my data will be processed in accordance with&nbsp;
                    <a href="https://getnexar.com/privacy/" target="_blank" rel="noopener noreferrer">
                      Nexar's Privacy Policy
                    </a>
                    &nbsp;and{' '}
                    <a href="https://www.getnexar.com/cookie" target="_blank" rel="noopener noreferrer">
                      Cookie Policy
                    </a>
                    . I agree to use the services according to&nbsp;
                    <a href="https://getnexar.com/terms/" target="_blank" rel="noopener noreferrer">
                      Nexar’s Terms of Service
                    </a>
                  </section>
                )}
              </div>
            </div>
          );
        }
      }
    )
  )
);

export default LoginPage;
